import type { AdType } from '@/modules/adv/types'
import type { AdProviderTypes } from '@/types'
import type { LoggerEvent } from '@/types/logger.types'

import { AdConsoleLogger } from '../utils/ad-console-logger'
import type { HbAdUnitDefinition, HeaderBiddingAuctionResult, PendingHbAuction } from './types'

const AUCTION_CACHE_TTL_CAP_MS = 300_000
const AUCTION_CACHE_TTL_BUFFER_MS = 30_000
const EMPTY_AUCTION_CACHE_TTL_MS = 60_000

export type AuctionCacheContext = { type: AdType; provider: AdProviderTypes; waterfallIndex: number }

function getAuctionExpiresAt({ winningBid, timestamp }: HeaderBiddingAuctionResult) {
    return winningBid
        ? winningBid.responseTimestamp +
              Math.min(AUCTION_CACHE_TTL_CAP_MS, winningBid.ttl * 1000 - AUCTION_CACHE_TTL_BUFFER_MS)
        : (timestamp ?? Date.now()) + EMPTY_AUCTION_CACHE_TTL_MS
}

function getKey(context: AuctionCacheContext): string {
    return `${context.type}:${context.waterfallIndex}:${context.provider}`
}

export class AuctionCache {
    private readonly entries = new Map<string, PendingHbAuction & { expiresAt: number }>()

    private readonly adConsoleLogger = new AdConsoleLogger('hb', 'AuctionCache')

    constructor(
        private readonly runAuction: (
            adUnit: HbAdUnitDefinition,
            context: { type: AdType; provider: AdProviderTypes },
        ) => Promise<HeaderBiddingAuctionResult | undefined>,
        private readonly logEvent: (event: LoggerEvent) => void,
    ) {}

    takeOrRun(
        getAdUnit: () => HbAdUnitDefinition | undefined,
        context: AuctionCacheContext,
    ): PendingHbAuction | undefined {
        const key = getKey(context)
        const cached = this.entries.get(key)
        this.entries.delete(key)

        if (cached && Date.now() < cached.expiresAt) {
            this.logCacheEvent('hit', context)
            return { adUnit: cached.adUnit, result: cached.result }
        }

        const adUnit = getAdUnit()
        if (!adUnit) {
            return undefined
        }

        this.logCacheEvent(cached ? 'expired' : 'miss', context)

        return { adUnit, result: this.runAuction(adUnit, { type: context.type, provider: context.provider }) }
    }

    clear(): void {
        this.entries.clear()
    }

    put(pending: PendingHbAuction, context: AuctionCacheContext): void {
        pending.result.then(
            (auctionResult) => {
                // rejected/timed-out auctions and results already past their TTL are dropped
                if (!auctionResult) {
                    return
                }
                const expiresAt = getAuctionExpiresAt(auctionResult)
                if (expiresAt > Date.now()) {
                    this.entries.set(getKey(context), { ...pending, expiresAt })
                }
            },
            () => {},
        )
    }

    private logCacheEvent(status: 'hit' | 'miss' | 'expired', context: AuctionCacheContext) {
        this.adConsoleLogger.debug(`auction cache ${status}`, context)
        this.logEvent({
            event: 'custom_event',
            eventName: 'ad_hb_auction_cache',
            adPlacement: context.type,
            adProvider: context.provider,
            waterfallIndex: context.waterfallIndex + 1,
            dimension1: status,
        })
    }
}
